import { Box, Flex, useBreakpointValue } from "@chakra-ui/react";
import Head from "next/head";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getSiteBaseUrl } from "@/config/config";
import { useGetDevelopmentsQuery, useGetPropertiesQuery } from "@/hooks/microservicesApi";
import BrandingChip from "@/sections/buscar/BrandingChip";
import MapPane from "@/sections/buscar/MapPane";
import MobileViewToggle from "@/sections/buscar/MobileViewToggle";
import { pageDefaultsFrom } from "@/sections/buscar/parseSearchQuery";
import { filterResetPatchFor, hasAnyActiveFilter } from "@/sections/buscar/ResetFiltersButton";
import ResultsPane from "@/sections/buscar/ResultsPane";
import { SearchPageContext } from "@/sections/buscar/SearchPageContext";
import TopBar from "@/sections/buscar/TopBar";
import useDrawPolygon from "@/sections/buscar/useDrawPolygon";
import Header from "@/sections/home/Header";

import deriveSearchTitle from "./deriveSearchTitle";
import { SearchHoverProvider } from "./SearchHoverContext";
import useSearchState, {
    computeGraceM,
    EMPTY_STATE,
    VIEWPORT_FILTERS_ON_DRAG,
} from "./useSearchState";

const BuscarLayout = ({ initialParams, favorites, session, config = null }) => {
    // Page identity for dedicated search pages (city/type presets). DEFAULTS
    // must be EMPTY_STATE *by reference* when there is no config — downstream
    // default-comparisons rely on that identity to keep /Buscar byte-identical.
    const searchPage = useMemo(
        () => ({
            config,
            DEFAULTS: config ? { ...EMPTY_STATE, ...pageDefaultsFrom(config) } : EMPTY_STATE,
        }),
        [config]
    );
    // P6.4 Re-centrar gate, also feeds useSearchState (D17 amend / D21): when
    // the camera has drifted off the auto-fit polygon position, apiBody flips
    // from polygon-as-filter back to viewport-as-filter (Zillow pattern —
    // list = what's on this map, polygon stays as a visual anchor).
    // Re-centrar / new draw / new place pick flips this false again.
    const [cameraDragged, setCameraDragged] = useState(false);
    // Debug overlay — opt-in via env. Was URL-driven (`?debug=1`) but
    // useSearchState's URL sync strips unknown keys on every drag/zoom/pick,
    // so the flag wouldn't survive the first map idle. Gate on a build-time
    // env var so it persists across all interactions. To enable locally, add
    // to `.env` (gitignored — won't ship): NEXT_PUBLIC_BUSCAR_DEBUG=1
    // The toggle pill still controls visibility — env=1 just makes the pill
    // appear; tap it to actually paint the layers. Branch-only; never pushed.
    const debugEnabled = process.env.NEXT_PUBLIC_BUSCAR_DEBUG === "1";
    const [debugVisible, setDebugVisible] = useState(false);
    // Mobile view toggle: "list" (default) shows the results grid first,
    // with a floating segmented pill at the bottom to switch to the
    // full-screen map. State lives at the layout level so both panes can
    // read it and the toggle pill can flip it. Desktop ignores this
    // entirely — at lg+ the panes render side-by-side regardless of
    // viewMode.
    const [viewMode, setViewMode] = useState("list");
    // ssr:false so Next hydrates side-by-side on desktop without a layout
    // shift. Returns `undefined` on server, then resolves correctly client-
    // side; treat undefined as desktop (no mobile hiding) so the SSR markup
    // matches the lg+ render and no pane gets hidden during hydration.
    const isMobile = useBreakpointValue(
        { base: true, lg: false },
        { ssr: typeof window === "undefined" }
    );
    // List view (mobile, until a desktop list/map toggle exists) hides the
    // map; apiBody drops the viewport spatial filter so the list reflects
    // the backend's "everything matching" set, not whatever the invisible
    // map's lat/lng/radius happens to be.
    const mapHidden = isMobile === true && viewMode !== "map";
    const { state, setFilter, clearFilter, setFilters, apiBody, userPolygons } = useSearchState(
        initialParams,
        {
            cameraDragged,
            mapHidden,
            defaults: searchPage.DEFAULTS,
            // "anchor" pages paint + frame their default boundary but scope
            // results by their other defaults (company) instead of it.
            floorFilters: config?.floorMode !== "anchor",
        }
    );

    // Favorites wiring is fleshed out in P4. ResultsPane gets the initial array.
    const [userFavorites, _setUserFavorites] = useState(favorites || []);

    // Map viewport bounds (google.maps.LatLngBounds). Lifted out of MapPane so
    // ResultsPane can filter its grid to listings whose lat/lng falls inside
    // the current viewport — Zillow pattern, the "list = what's on this map"
    // invariant. Captured on every `onIdle` from MapPane. Null until the map
    // first settles, during which window ResultsPane shows the unfiltered set.
    const [bounds, setBounds] = useState(null);
    // Stable callback so MapPane's `onIdle` doesn't see a fresh function
    // reference each render — avoids unnecessary effect work down there.
    const handleBoundsChange = useCallback((b) => setBounds(b), []);

    // Search boundary stack (P8.5 multi-polygon). Each entry is
    // `{ shape: 'circle' | 'polygon', coords, origin: 'place' | 'draw' }`.
    // Lives in useSearchState — drawn polygons round-trip via D20's repeated
    // `polygon=lat,lng;...` URL keys; place-derived circles round-trip via
    // q+lat+lng+radio (derivePolygons in the SSR parser picks the URL polygons
    // first, falls back to a single place circle). D17 still classifies
    // polygons as map state for the Limpiar gate: ResetFiltersButton ignores
    // them. setPolygons funnels writes through setFilter/clearFilter so URL
    // sync is automatic.
    const polygons = state.polygons;
    const setPolygons = useCallback(
        (next) => {
            if (!Array.isArray(next) || next.length === 0) clearFilter("polygons");
            else setFilter("polygons", next);
        },
        [setFilter, clearFilter]
    );

    // Captured google.maps.Map instance — captured from inside <GoogleMap> via
    // useMap() (only resolves under that context, so MapPane mounts a tiny
    // CaptureMapRef child that writes here). Lets the layout call imperative
    // map APIs (fitBounds for the auto-polygon path, P6.4 Re-centrar) without
    // pushing every camera-shaping call down to MapPane.
    const mapRef = useRef(null);

    // Camera placement: setZoom + setCenter, computed manually. Initially
    // used fitBounds(circle.getBounds(), { padding }) but Google's fitBounds
    // does not reliably honor pixel padding when the bounds aspect ratio
    // doesn't match the container — verified empirically: at padding=160
    // on a 600px-tall container, the rendered circle still touched the
    // top/bottom edges (only ~17px margin instead of 160). Manual math
    // gives pixel-accurate margins.
    // Just enough for a marker bubble at the filter perimeter not to bump
    // the viewport edge. Tight on purpose — the polygon takes the full
    // viewport otherwise wasted on aesthetic padding.
    const CIRCLE_VIEWPORT_PADDING_PX = 16;
    // Snapshot of the last camera position we observed settling — written by
    // every idle, every auto-fit (fitMapToCircle, fitMapToPolygon), and the
    // handleDrawFinish camera capture. handleMapIdle compares each new
    // settled camera against this snapshot to detect user-initiated zoom
    // changes (mousewheel, +/-, double-click — none of which fire dragstart).
    // Auto-fits land exactly on the value we wrote, so they don't false-
    // positive. Cold-load mousewheel zooms work because the first idle
    // initializes the ref, and the second idle compares against it.
    // (D22 — was previously `lastFitRef`, written only by auto-fits, which
    // missed mousewheel zooms on cold-load before any auto-fit had run.)
    const lastSettledCameraRef = useRef(null);
    // Set true by fitMapToCircle/Polygon/handleDrawFinish before they move the
    // camera programmatically; cleared by the next handleMapIdle. The idle
    // that fires after a programmatic move skips its divergence check because
    // Google Maps snaps the camera to discrete steps (vector zoom can land on
    // a different fractional value than what we passed to setZoom, and the
    // delta exceeds our 0.05 epsilon), false-positiving cameraDragged=true on
    // every initial place-pick auto-fit.
    const skipNextDivergenceRef = useRef(false);

    // Reusable helper: position the camera so a circle of (center, radiusM)
    // fits the visible map with CIRCLE_VIEWPORT_PADDING_PX of breathing room
    // on every side. Used by both applyPlaceBoundary (place-pick → new
    // boundary) and recenterToBoundary (P6.4 — user clicks Re-centrar
    // after panning/zooming off the polygon).
    const fitMapToCircle = useCallback((center, radiusM) => {
        const map = mapRef.current;
        if (!map) return;
        const div = map.getDiv?.();
        if (!div) return;
        // When the map is display:none (mobile list view) the container has
        // zero size; setZoom + panTo go nowhere AND the math underflows.
        // Writing lastSettledCameraRef to the unreached target would also
        // false-positive cameraDragged on the next list→map toggle. Skip
        // entirely — the list→map auto-fit useEffect will retry once the
        // container is measurable.
        if (div.clientWidth === 0 || div.clientHeight === 0) return;
        // Smaller dimension wins — the circle is round, so the constraining
        // axis is whichever map edge is closer.
        const padded = Math.min(
            div.clientWidth - 2 * CIRCLE_VIEWPORT_PADDING_PX,
            div.clientHeight - 2 * CIRCLE_VIEWPORT_PADDING_PX
        );
        if (padded <= 0) return;
        // Web Mercator meters-per-pixel at zoom Z and latitude L:
        //   mpp(Z, L) = 156543.03392 * cos(L) / 2^Z
        // We want 2 * radiusM meters to fit in `padded` pixels:
        //   2 * radiusM = padded * mpp(Z, L)
        //   Z = log2(padded * 156543.03392 * cos(L) / (2 * radiusM))
        const cosLat = Math.cos((center.lat * Math.PI) / 180);
        const targetZoom = Math.log2((padded * 156543.03392 * cosLat) / (2 * radiusM));
        // setZoom first (snap to scale), then panTo (built-in smooth ease,
        // ~300ms). For re-centers near the current zoom this reads as a
        // smooth slide; for big zoom jumps the zoom still snaps but the pan
        // glides. Tier 1 polish — full fly-to (animated zoom too) would
        // require a requestAnimationFrame tween (DW.smooth-camera).
        map.setZoom(targetZoom);
        map.panTo(center);
        // Snapshot for handleMapIdle's divergence check. Google may snap the
        // settled zoom to a different value than `targetZoom` (vector zoom
        // discretization), so we also raise the skip-next-divergence flag —
        // handleMapIdle's first idle after this fit will re-record the
        // actual settled position as the new baseline.
        lastSettledCameraRef.current = {
            lat: center.lat,
            lng: center.lng,
            zoom: targetZoom,
        };
        skipNextDivergenceRef.current = true;
    }, []);

    // Same idea as fitMapToCircle but for a vertex array (manual draw). Computes
    // the bbox, picks the axis that bottoms out first against the padded
    // viewport, sets zoom accordingly. Same Mercator mpp formula — the
    // projection is locally conformal so width and height share the same scale
    // at the bbox center latitude.
    const fitMapToPolygon = useCallback((vertices) => {
        const map = mapRef.current;
        if (!map || !Array.isArray(vertices) || vertices.length < 1) return;
        const div = map.getDiv?.();
        if (!div) return;
        // See fitMapToCircle — skip when the container isn't measurable so
        // we don't pollute lastSettledCameraRef with an unreached target.
        if (div.clientWidth === 0 || div.clientHeight === 0) return;
        let minLat = Infinity;
        let maxLat = -Infinity;
        let minLng = Infinity;
        let maxLng = -Infinity;
        for (const v of vertices) {
            if (!v || !Number.isFinite(v.lat) || !Number.isFinite(v.lng)) continue;
            if (v.lat < minLat) minLat = v.lat;
            if (v.lat > maxLat) maxLat = v.lat;
            if (v.lng < minLng) minLng = v.lng;
            if (v.lng > maxLng) maxLng = v.lng;
        }
        if (!Number.isFinite(minLat) || !Number.isFinite(minLng)) return;
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        const cosLat = Math.cos((centerLat * Math.PI) / 180);
        // Convert bbox extents to meters. Latitude is uniform (~111.32 km/deg);
        // longitude shrinks by cos(lat) toward the poles.
        const heightM = (maxLat - minLat) * 111320;
        const widthM = (maxLng - minLng) * 111320 * cosLat;
        const availW = div.clientWidth - 2 * CIRCLE_VIEWPORT_PADDING_PX;
        const availH = div.clientHeight - 2 * CIRCLE_VIEWPORT_PADDING_PX;
        if (availW <= 0 || availH <= 0) return;
        // Per-axis target zooms; smaller wins (that axis is the constraint).
        // Degenerate axis (single point along it) → Infinity → ignored.
        const zW = widthM > 0 ? Math.log2((availW * 156543.03392 * cosLat) / widthM) : Infinity;
        const zH = heightM > 0 ? Math.log2((availH * 156543.03392 * cosLat) / heightM) : Infinity;
        let targetZoom = Math.min(zW, zH);
        if (!Number.isFinite(targetZoom)) return;
        // Cap to Google's max zoom so degenerate (zero-area) polygons don't
        // blow past it.
        if (targetZoom > 21) targetZoom = 21;
        const center = { lat: centerLat, lng: centerLng };
        // setZoom first (snap to scale), then panTo (built-in smooth ease,
        // ~300ms). For re-centers near the current zoom this reads as a
        // smooth slide; for big zoom jumps the zoom still snaps but the pan
        // glides. Tier 1 polish — full fly-to (animated zoom too) would
        // require a requestAnimationFrame tween (DW.smooth-camera).
        map.setZoom(targetZoom);
        map.panTo(center);
        lastSettledCameraRef.current = {
            lat: centerLat,
            lng: centerLng,
            zoom: targetZoom,
        };
        skipNextDivergenceRef.current = true;
    }, []);

    // Place-pick → REPLACE the entire polygon stack with a single place
    // circle (D17 + P8.5). New place pick semantically starts the search
    // over from a different neighborhood; preserving stale drawn polygons
    // would be confusing. Pass `null` to clear.
    const applyPlaceBoundary = useCallback(
        (boundary) => {
            if (!boundary) {
                setPolygons([]);
                return;
            }
            const { center, radiusM, rawRadiusM } = boundary;
            const valid =
                center &&
                Number.isFinite(center.lat) &&
                Number.isFinite(center.lng) &&
                Number.isFinite(radiusM);
            if (!valid) {
                setPolygons([]);
                return;
            }
            const newPolygon = {
                shape: "circle",
                coords: {
                    center,
                    radius: radiusM,
                    // Google's pre-clamp/cap half-diagonal — kept on the
                    // polygon entry exclusively for the debug overlay
                    // (?debug=1). Null when not available (e.g. cold-load
                    // hydration from URL — only the clamped value round-trips).
                    rawRadius: Number.isFinite(rawRadiusM) ? rawRadiusM : null,
                },
                origin: "place",
            };
            setPolygons([newPolygon]);
            // Auto-fit camera to the grace-inflated radius so map view and
            // list view show the same result count — bounds-clip narrows the
            // map's items to the camera viewport, and apiBody's filter
            // includes a +grace buffer (D21). Visual circle in BoundaryOverlay
            // still paints the strict polygon.coords.radius.
            fitMapToCircle(center, radiusM + computeGraceM(newPolygon));
            setCameraDragged(false);
        },
        [fitMapToCircle, setPolygons]
    );

    // P7.1 "Ampliar búsqueda": empty-state CTA in ResultsPane. Resets
    // filters AND boundary AND location text in a single setFilters call
    // (one URL sync). Map state (lat/lng/zoom/radius) is intentionally
    // preserved — user keeps the view they were looking at, just sees what
    // actually exists there. Gated on `canAmpliar` below so the button
    // only renders when there's something to reset.
    const canAmpliar = hasAnyActiveFilter(state, searchPage.DEFAULTS) || userPolygons.length > 0;
    const ampliarBusqueda = useCallback(() => {
        setFilters({
            ...filterResetPatchFor(searchPage.DEFAULTS),
            q: "",
            city: "",
            neighborhood: "",
            // Revert to the page's default boundary, not to nationwide —
            // on a city page "Ampliar búsqueda" widens back to the city.
            polygons: searchPage.DEFAULTS.polygons,
        });
    }, [setFilters, searchPage.DEFAULTS]);

    // "Borrar perímetros": drop the entire stack AND the location text
    // (q + city + neighborhood) per D17 rule 3 — the boundary is anchored
    // to a location, so clearing one drops the other. Camera untouched
    // (lat/lng/zoom/radius are map state, separate from filter/location
    // state — drag-the-map mental model stays intact).
    const clearBoundary = useCallback(() => {
        setPolygons([]);
        setFilters({ q: "", city: "", neighborhood: "" });
    }, [setFilters, setPolygons]);

    // P6.4 "Re-centrar": after the user has dragged the camera off the
    // active boundary, snap back to frame the polygon. Reads the current
    // polygon state (shape='circle' for the auto-boundary path; the
    // shape='polygon' branch lands with manual draw in P6.5+).
    // P6.5 manual draw. Hook owns isDrawing + accumulated vertices + map
    // click/dblclick/Esc listeners. onFinish (≥3 vertices) commits the
    // shape as the active polygon. Per D17 rule 6 manual draw isn't tied
    // to a place name, so we also clear q/city/neighborhood. Camera stays
    // put (D17 UX matrix: "Draw → Aplicar" leaves bounds unchanged) —
    // user drew where they were looking; respect that.
    // Manual draw APPENDS to the polygon stack (P8.5 — "Dibujar perímetro
    // adicional"). q/city/neighborhood stay intact so the searchbar still
    // shows the original place anchor when the user composed place + draws;
    // apiBody drops those text filters when ANY drawn polygon exists, which
    // is where the over-constrain bug would otherwise bite. Camera stays
    // put — user drew where they were looking; snapshot it so the next
    // onIdle divergence check doesn't false-positive Re-centrar.
    const handleDrawFinish = useCallback(
        (drawnVertices) => {
            const newPolygon = { shape: "polygon", coords: drawnVertices, origin: "draw" };
            // Append to the USER stack only: on a dedicated city page the
            // default boundary (origin 'floor') must be REPLACED by a draw,
            // never OR-ed with it — the backend unions polygons, so floor +
            // inner draw would widen back to the whole city.
            setPolygons([...userPolygons, newPolygon]);
            const map = mapRef.current;
            if (map) {
                const c = map.getCenter?.();
                const z = map.getZoom?.();
                if (c && z != null) {
                    lastSettledCameraRef.current = {
                        lat: c.lat(),
                        lng: c.lng(),
                        zoom: z,
                    };
                }
            }
            setCameraDragged(false);
        },
        [userPolygons, setPolygons]
    );
    const draw = useDrawPolygon({ mapRef, onFinish: handleDrawFinish });

    // Re-centrar: fit camera to the union bbox of all polygons. fitMapToPolygon
    // expands each polygon (circles → bbox corners from center±(radius+grace),
    // drawn polygons → vertices) to a vertex list, then bounds-fits the union.
    // Grace is the same buffer apiBody adds to the backend filter (D21) — so
    // the camera frames the FULL set of properties the backend will return,
    // not just the strict visual circle. Without it, bounds-clip would hide
    // grace-zone properties (between visualRadius and visualRadius+grace) and
    // the map count would trail the list count by 5-15 results.
    //
    // Auto-fit fires when the map is (or becomes) visible with active
    // polygons:
    //   - Mobile cold-load: viewMode='list', map hidden. User toggles to
    //     map → mapHidden flips true→false → fire. Camera fits to polygons.
    //   - Desktop cold-load: side-by-side, map visible from mount. First
    //     useEffect run (prev=undefined) → fire. Without this, desktop
    //     would land at the URL's z (often tighter than the polygon's
    //     grace-inflated radius) and bounds-clip would hide grace-zone
    //     properties, making desktop count < mobile count.
    //   - Subsequent re-runs while map stays visible (cameraDragged
    //     toggles, polygons changes) don't fire — we only auto-fit on
    //     the visibility transition or first mount. Otherwise dragging
    //     and then having a state change (e.g. filter toggle) would
    //     yank the camera back to the polygon.
    //
    // Gated on cameraDragged=false so a user who intentionally moved the
    // camera doesn't get their position clobbered.
    //
    // Deferred via requestAnimationFrame because the display:none →
    // display:block flip and the auto-fit math both happen in the same
    // commit; Google Maps needs to re-measure the container size before
    // fitMapToCircle/Polygon's pixel math produces a correct zoom.
    //
    // On cold-mount, mapRef.current is null for the first ~hundreds of ms
    // — vis.gl's useMap() doesn't resolve until Google Maps' async load
    // completes, and only then does CaptureMapRef's useEffect write the
    // map instance into the ref. A single rAF won't see it; we poll for
    // up to ~30 frames (~500ms) before giving up. Without this the cold-
    // load camera stays at the URL's `z` (or DEFAULT_ZOOM=12) and never
    // gets the proper polygon-aware fit — visible as a too-wide-or-tight
    // framing of the filter circle on first paint.
    const prevMapHiddenRef = useRef(undefined);
    useEffect(() => {
        const prev = prevMapHiddenRef.current;
        prevMapHiddenRef.current = mapHidden;
        // Map is hidden → nothing to fit.
        if (mapHidden) return;
        // Map is already visible AND was visible last run → unrelated
        // re-run (cameraDragged or polygons.length changed mid-session).
        // Skip so we don't yank the camera around.
        if (prev === false) return;
        // Either first run (prev=undefined → desktop initial mount) or
        // visibility transition (prev=true → mobile toggle to map).
        let cancelled = false;
        let attempts = 30;
        const tryFit = () => {
            if (cancelled) return;
            const map = mapRef.current;
            if (!map) {
                attempts -= 1;
                if (attempts > 0) requestAnimationFrame(tryFit);
                return;
            }
            // Tell Google Maps the container size may have changed (it may
            // have been display:none and is now display:block). Without
            // this, vis.gl marker overlay positions can drift relative to
            // the underlying lat/lng because the projection cache wasn't
            // invalidated. (See also markersEpoch remount in MapPane.)
            if (typeof window !== "undefined" && window.google?.maps?.event) {
                window.google.maps.event.trigger(map, "resize");
            }
            // Reads ALL polygons on purpose: a dedicated page's default
            // boundary (origin 'floor') must frame the camera on cold load
            // exactly like a user boundary would.
            if (polygons.length > 0 && !cameraDragged) {
                recenterToBoundaryRef.current?.();
            }
        };
        requestAnimationFrame(tryFit);
        return () => {
            cancelled = true;
        };
    }, [mapHidden, polygons.length, cameraDragged]);
    const recenterToBoundaryRef = useRef(null);

    const recenterToBoundary = useCallback(() => {
        if (!Array.isArray(polygons) || polygons.length === 0) return;
        const allVerts = [];
        for (const p of polygons) {
            if (p.shape === "circle" && p.coords?.center) {
                const { center, radius } = p.coords;
                const fitRadius = radius + computeGraceM(p);
                const cosLat = Math.cos((center.lat * Math.PI) / 180) || 1;
                const dLat = fitRadius / 111_320;
                const dLng = fitRadius / (111_320 * cosLat);
                allVerts.push(
                    { lat: center.lat + dLat, lng: center.lng + dLng },
                    { lat: center.lat + dLat, lng: center.lng - dLng },
                    { lat: center.lat - dLat, lng: center.lng + dLng },
                    { lat: center.lat - dLat, lng: center.lng - dLng }
                );
            } else if (p.shape === "polygon" && Array.isArray(p.coords)) {
                allVerts.push(...p.coords);
            }
        }
        if (allVerts.length === 0) return;
        fitMapToPolygon(allVerts);
        setCameraDragged(false);
    }, [polygons, fitMapToPolygon]);
    // Keep the ref in sync with the latest callback so the viewMode-change
    // useEffect above can fire-and-forget without redeclaring its deps.
    recenterToBoundaryRef.current = recenterToBoundary;

    // "Map is being dragged" → ResultsPane shows skeletons. Set on dragstart,
    // cleared on idle (NOT dragend) so the skeleton stays on through the brief
    // gap between releasing the mouse and bounds being applied — otherwise
    // the user sees one frame of stale (old-bounds) results before the
    // viewport filter kicks in. The Zillow rhythm: drag → blank → refill.
    const [isMapDragging, setIsMapDragging] = useState(false);
    const handleMapDragStart = useCallback(() => {
        setIsMapDragging(true);
        setCameraDragged(true);
    }, []);

    // On every map idle: clear the dragging flag, detect camera changes via
    // divergence vs the last settled position, push the current camera
    // (lat, lng, zoom, radius) to state ONLY when the user has actually
    // interacted (cameraDragged true). Cold-load default settles don't
    // pollute state — D22.
    //
    // Why the gate matters: the map mounts at DEFAULT_CENTER (Guadalajara,
    // z=12). Without the gate, the first idle would push that camera into
    // state, the apiBody would send `lat/lng/radius` to the backend, and a
    // cold-load /Buscar with no URL params would filter results to
    // "around Guadalajara" instead of "all properties anywhere." Gating on
    // cameraDragged keeps state.lat/lng/radius null until the user actively
    // pans/zooms, at which point the backend's spatial filter kicks in.
    //
    // The `lastSettledCameraRef` is initialized on the first idle (regardless
    // of cameraDragged), so subsequent mousewheel zooms can be detected via
    // divergence — without this, mousewheel from cold-load wouldn't flip
    // cameraDragged true (no dragstart event fires for mousewheel).
    //
    // Rounding to 4 decimals (~11m) is enough precision for the user but
    // wide enough that micro-jitter between drags lands on the same RTK
    // Query cache key (default `keepUnusedDataFor: 60s`).
    const handleMapIdle = useCallback(
        (camera) => {
            setIsMapDragging(false);
            if (!camera) return;
            const { lat, lng, zoom, radiusM } = camera;
            if (lat == null || lng == null || zoom == null) return;
            // P6.4 zoom-divergence detection: if the settled camera differs
            // from the last position we observed by a non-trivial amount,
            // the user moved the camera (pan, mousewheel, +/-, double-click).
            // Auto-fits write their target value to lastSettledCameraRef
            // before this fires, so they don't false-positive. Epsilons:
            // 0.0001° ≈ 11m at the equator (well below any meaningful pan);
            // 0.05 zoom levels covers float drift between setZoom and
            // getZoom while still firing on a single +/- press.
            const lastSettled = lastSettledCameraRef.current;
            const skipDivergence = skipNextDivergenceRef.current;
            skipNextDivergenceRef.current = false;
            if (lastSettled && !skipDivergence) {
                const latDiff = Math.abs(lat - lastSettled.lat);
                const lngDiff = Math.abs(lng - lastSettled.lng);
                const zoomDiff = Math.abs(zoom - lastSettled.zoom);
                if (latDiff > 0.0001 || lngDiff > 0.0001 || zoomDiff > 0.05) {
                    setCameraDragged(true);
                }
            }
            // Always record the current settled position so the next idle has
            // a baseline to compare against (covers cold-load → mousewheel
            // before any auto-fit has run). Note: when skipDivergence was
            // true, this records what Google actually settled at (which can
            // differ from the requested zoom due to vector zoom snapping) —
            // future idles compare against the real value.
            lastSettledCameraRef.current = { lat, lng, zoom };
            // D22 gate: only push camera state when the user has interacted.
            // Cold-load default camera doesn't pollute state.
            if (!cameraDragged) return;
            const r = (n) => Math.round(n * 10000) / 10000;
            const nextLat = r(lat);
            const nextLng = r(lng);
            // D15: search radius derives from the visible viewport. Backend
            // returns listings within a circle of this radius around lat/lng;
            // without scaling it with the user's zoom, panning to a wide view
            // leaves most of the map empty even when listings exist there
            // (verified: 5km radius at z=10.83 leaves Tonalá/Tlaquepaque and
            // the Zapopan outskirts blank).
            //
            // Round to nearest 1km — small jitter between drags shouldn't
            // invalidate the RTK Query cache. Floor at 1km (minimum sensible
            // search). No upper cap: zooming out to a wide view (e.g. all of
            // Mexico at z=4) needs to send a proportional radius so the
            // backend's `ST_Distance <= radius` WHERE doesn't strand the
            // user with 0 results. Perf-wise, `ST_Distance` isn't using the
            // spatial index efficiently regardless of value, so a wide
            // radius doesn't degrade much beyond a narrow one — the LIMIT
            // bounds returned rows either way. If implicit Postgres ordering
            // produces visibly clustered results at very wide zooms, the
            // fix is a backend default `ORDER BY` (separate ticket).
            const nextRadius =
                radiusM != null ? Math.max(1000, Math.round(radiusM / 1000) * 1000) : null;
            // The first round of state.latitude / longitude / zoom can be null
            // if the user hit a URL without those params — treat null as "any
            // new value differs", so the first idle commits the camera.
            if (
                state.latitude === nextLat &&
                state.longitude === nextLng &&
                state.zoom === zoom &&
                (nextRadius == null || state.radius === nextRadius)
            ) {
                return;
            }
            const patch = { latitude: nextLat, longitude: nextLng, zoom };
            if (nextRadius != null) patch.radius = nextRadius;
            setFilters(patch);
        },
        [setFilters, state.latitude, state.longitude, state.zoom, state.radius, cameraDragged]
    );

    const skipProperties = state.includeMode === "desarrollos";
    const skipDevelopments = state.includeMode === "propiedades";

    const {
        data: propertiesData,
        isFetching: isFetchingProperties,
        isSuccess: isPropertiesSuccess,
        isError: isPropertiesError,
    } = useGetPropertiesQuery(
        { body: { ...apiBody, includeStats: false } },
        { skip: skipProperties }
    );
    // RTK Query keeps the previously-cached data accessible even when `skip`
    // flips true — so without this guard, switching includeMode to "Solo
    // desarrollos"/"Solo propiedades" still mixes stale cached props/devs
    // into the grid. Explicitly empty whichever side is now skipped.
    const properties = skipProperties ? [] : (propertiesData?.properties ?? propertiesData ?? []);

    const {
        data: developmentsData,
        isFetching: isFetchingDevs,
        isSuccess: isDevsSuccess,
        isError: isDevsError,
    } = useGetDevelopmentsQuery({ body: apiBody }, { skip: skipDevelopments });
    const developments = skipDevelopments ? [] : (developmentsData ?? []);

    // Reveal results once each query has *resolved* — success OR error. A
    // failing partner (e.g. /search 500ing on a backend SQL bug for some
    // filter shape) shouldn't hang the entire pane forever in a skeleton
    // state; we still want the half that loaded to render. The gate's only
    // job is killing the partial-flash on initial load.
    const propertiesResolved = skipProperties || isPropertiesSuccess || isPropertiesError;
    const developmentsResolved = skipDevelopments || isDevsSuccess || isDevsError;
    const isFetching = isFetchingProperties || isFetchingDevs;
    const isWaitingFirstFetch = !propertiesResolved || !developmentsResolved;

    return (
        <>
            <Head>
                <title>
                    {deriveSearchTitle(state, {
                        fallbackTitle: config?.head?.title,
                        defaultType: searchPage.DEFAULTS.type,
                        fallbackLocation: config?.floor?.label,
                    })}
                </title>
                {config?.head?.canonicalPath && (
                    <link rel="canonical" href={getSiteBaseUrl() + config.head.canonicalPath} />
                )}
                {config?.head?.description && (
                    <meta name="description" content={config.head.description} />
                )}
            </Head>
            <SearchPageContext.Provider value={searchPage}>
                <SearchHoverProvider>
                    <Header />
                    {/* colorPalette="accent" scopes the Guia navy as the default
                    primary palette for this page only. Descendants use
                    bg/border/color = "colorPalette.solid|contrast|…" and they
                    resolve against accent.* semantic tokens defined in
                    customTheme.js — keeps the theme override one-line + one
                    Box wrapper instead of a nested ChakraProvider. */}
                    <Flex direction="column" h="100vh" pt="80px" colorPalette="accent">
                        {/* Mobile list view hides the map and with it the
                            floating branding card — this banner carries the
                            page identity there instead. Gating: viewMode is
                            "list" on server and first client render alike
                            (hydration-safe, unlike the breakpoint-derived
                            mapHidden), and the CSS breakpoint scopes it to
                            mobile — so it never coexists with the floating
                            card, which only shows when the map does. */}
                        {viewMode !== "map" && (
                            <Box hideFrom="lg">
                                <BrandingChip variant="banner" />
                            </Box>
                        )}
                        <TopBar
                            state={state}
                            setFilter={setFilter}
                            clearFilter={clearFilter}
                            setFilters={setFilters}
                            applyPlaceBoundary={applyPlaceBoundary}
                        />
                        <Flex flex="1 1 auto" overflow="hidden">
                            <MapPane
                                hidden={isMobile === true && viewMode !== "map"}
                                state={state}
                                properties={properties}
                                developments={developments}
                                isFetching={isFetching}
                                onBoundsChange={handleBoundsChange}
                                onMapDragStart={handleMapDragStart}
                                onMapIdle={handleMapIdle}
                                polygons={polygons}
                                userPolygons={userPolygons}
                                mapRef={mapRef}
                                onClearBoundary={clearBoundary}
                                onRecenterBoundary={recenterToBoundary}
                                cameraDragged={cameraDragged}
                                isDrawing={draw.isDrawing}
                                isDrawPressed={draw.isPressed}
                                drawVertices={draw.vertices}
                                onStartDraw={draw.start}
                                onCancelDraw={draw.cancel}
                                onApplyDraw={draw.finish}
                                debugEnabled={debugEnabled}
                                debugVisible={debugVisible}
                                onToggleDebug={() => setDebugVisible((v) => !v)}
                                debugPaddingPx={CIRCLE_VIEWPORT_PADDING_PX}
                            />
                            <ResultsPane
                                hidden={isMobile === true && viewMode !== "list"}
                                state={state}
                                setFilter={setFilter}
                                clearFilter={clearFilter}
                                properties={properties}
                                developments={developments}
                                isFetching={isFetching}
                                isWaitingFirstFetch={isWaitingFirstFetch}
                                session={session}
                                userFavorites={userFavorites}
                                bounds={bounds}
                                isMapDragging={isMapDragging && VIEWPORT_FILTERS_ON_DRAG}
                                canAmpliar={canAmpliar}
                                onAmpliar={ampliarBusqueda}
                                mapHidden={isMobile === true && viewMode !== "map"}
                                onShowMap={() => setViewMode("map")}
                                applyBoundsClip={
                                    VIEWPORT_FILTERS_ON_DRAG &&
                                    !(isMobile === true && viewMode !== "map") &&
                                    cameraDragged
                                }
                                userPolygons={userPolygons}
                                onClearBoundary={clearBoundary}
                            />
                        </Flex>
                    </Flex>
                    {/* Hide the floating Map/Lista toggle while drawing — the
                    user is committed to the map surface, and the toggle
                    visually competes with DrawBanner's Cancelar/Aplicar
                    actions for the bottom of the viewport. */}
                    {!draw.isDrawing && (
                        <MobileViewToggle viewMode={viewMode} onChange={setViewMode} />
                    )}
                    {/* Dev-only: surface a quick view of the params SSR produced */}
                    <Box display="none" data-debug-initial-params>
                        {JSON.stringify(initialParams)}
                    </Box>
                </SearchHoverProvider>
            </SearchPageContext.Provider>
        </>
    );
};

export default BuscarLayout;

// This file lives under src/pages/ for historical reasons but is a shared
// module, not a route. Next.js still registers it as a page; serve 404 for
// its accidental URL instead of prerendering it (which crashes the build now
// that the app really renders during SSR).
export function getStaticProps() {
    return { notFound: true };
}
